import { useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { Box } from '@mui/material';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { isVideoFile } from '@modules/_shared/components/PropertyFiles';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { ListingPropertyImages } from '@modules/listings/ListingPropertyImages/ListingPropertyImages';
import { GET_PROPERTY_IMAGES_COUNT } from '@modules/listings/ListingPropertyImages/listingPropertyQueries';
import { LISTING_DOCUMENTS } from '@modules/listings/lotsQueries';
import { useAutoTour } from '@modules/product-updates/tours/TourProvider';
import { useLocale } from '@shared/hooks/locale';

import { ListingFilesMasterDetail } from './ListingFilesMasterDetail';
import { MediaHeader, type MediaKind } from './MediaHeader';
import {
  type MediaUploadResult,
  useListingMediaUpload,
} from './useListingMediaUpload';

export const ListingMedia = ({
  lotId,
  propertyId,
  readOnly = false,
}: {
  lotId: string;
  propertyId?: string | null;
  readOnly?: boolean;
}) => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const splat = params['*'] ?? '';
  const activeTab: MediaKind = splat.startsWith('videos')
    ? 'videos'
    : splat.startsWith('documents')
    ? 'documents'
    : 'images';

  useAutoTour('listing-watermark', !readOnly && activeTab === 'images');

  const { pathname } = useLocation();
  const mediaBase = pathname.slice(
    0,
    pathname.indexOf('/media') + '/media'.length,
  );

  const [snackbar, setSnackbar] = useState<{
    message: string;
    severity: 'success' | 'error' | 'warning';
  } | null>(null);

  const { data: imagesData } = useQuery(GET_PROPERTY_IMAGES_COUNT, {
    variables: { property_id: propertyId ?? '' },
    skip: propertyId == null,
  });
  const { data: filesData } = useQuery(LISTING_DOCUMENTS, {
    variables: { lotId },
  });

  const files = filesData?.lots_by_pk?.property.property_files ?? [];
  const videoCount = files.filter(propertyFile =>
    isVideoFile(propertyFile.file),
  ).length;

  const counts: Record<MediaKind, number> = {
    images: imagesData?.property_images_aggregate.aggregate?.count ?? 0,
    videos: videoCount,
    documents: files.length - videoCount,
  };

  const { uploadFiles, isUploading } = useListingMediaUpload({
    propertyId,
  });

  const summarize = (result: MediaUploadResult): string => {
    const parts: string[] = [];
    if (result.images > 0) {
      parts.push(t('{{count}} images', { count: result.images }));
    }
    if (result.videos > 0) {
      parts.push(t('{{count}} videos', { count: result.videos }));
    }
    if (result.documents > 0) {
      parts.push(t('{{count}} documents', { count: result.documents }));
    }
    return parts.join(', ');
  };

  const handleUpload = async (selected: File[]) => {
    const result = await uploadFiles(selected);
    const uploaded = result.images + result.videos + result.documents;

    if (uploaded === 0) {
      setSnackbar({
        message: t('No files could be uploaded'),
        severity: 'error',
      });
      return;
    }

    setSnackbar({
      message:
        result.failed > 0
          ? t('Uploaded {{summary}} ({{count}} failed)', {
              summary: summarize(result),
              count: result.failed,
            })
          : t('Uploaded {{summary}}', { summary: summarize(result) }),
      severity: result.failed > 0 ? 'warning' : 'success',
    });

    const usedKinds = (['images', 'videos', 'documents'] as MediaKind[]).filter(
      kind => result[kind] > 0,
    );
    if (usedKinds.length === 1 && usedKinds[0] !== activeTab) {
      navigate({
        pathname: `${mediaBase}/${usedKinds[0]}`,
        search: searchParams.toString(),
      });
    }
  };

  const header = (
    <MediaHeader
      counts={counts}
      activeTab={activeTab}
      onUpload={handleUpload}
      isUploading={isUploading}
      readOnly={readOnly}
    />
  );

  return (
    <Box sx={{ height: '100%', minHeight: 0 }}>
      <Routes>
        <Route
          index
          element={
            <Navigate
              to={{ pathname: 'images', search: searchParams.toString() }}
              replace
            />
          }
        />
        <Route
          path="images/*"
          element={
            <ListingPropertyImages
              propertyId={propertyId}
              lotId={lotId}
              readOnly={readOnly}
              disableUpload
              leftColumnHeader={header}
            />
          }
        >
          <Route path=":imgId" />
        </Route>
        {(['videos', 'documents'] as const).map(fileKind => (
          <Route
            key={fileKind}
            path={`${fileKind}/*`}
            element={
              <ListingFilesMasterDetail
                lotId={lotId}
                fileKind={fileKind}
                readOnly={readOnly}
                leftColumnHeader={header}
              />
            }
          />
        ))}
      </Routes>

      {snackbar != null && (
        <RASnackbar
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={() => setSnackbar(null)}
        />
      )}
    </Box>
  );
};

export default ListingMedia;
